import { FC, ReactElement, useContext, useEffect, useMemo, useState } from 'react';
import {Activity} from "../../../../types";
import {LabelsContext, useLabels} from "../../../../hooks/Labels";
import {RadarChartOutlined, TableOutlined} from "@ant-design/icons";
import {Button, Card, Select, Space, Typography} from "antd";
import { SectionPerformanceRadarPlotCard } from './SectionPerformanceRadarPlotCard';
import {FeedbackRowProps} from "../../ActivityFeedbackRows/types";
import {useFilterPrefix} from "../../../DataTable/Filters/FilterPrefixProvider";
import {QueryFilter} from "../../../DataTable/Filters/types";
import {useQuerySearchParams} from "../../../../config/queryClient";
import { SpinContainer } from '../../../SpinContainer';
import { SectionPerformanceFirstBestTableCard } from './SectionPerformanceDataTableCard';


const {Text} = Typography;

export interface Props extends FeedbackRowProps {
    type: 'conversation' | 'dialogue-choices',
}

interface CardTitleProps {
    titleText: string;
    showChart: boolean;
    setShowChart:  React.Dispatch<React.SetStateAction<boolean>>;
    selectedActivityId?: string;
    setSelectedActivityId?: React.Dispatch<React.SetStateAction<string | undefined>>;
    activityOptions?: {label: string; value: string}[];
}

const CardTitle: FC<CardTitleProps> = ({
    showChart,
    setShowChart,
    titleText,
    selectedActivityId,
    setSelectedActivityId,
    activityOptions,
}): ReactElement => {
    const {t} = useLabels();
    return (
        <div style={{width: '100%', flexDirection: 'column', display: 'flex', gap: 8}}>
            <Space style={{justifyContent: "space-between", width: '100%'}}>
                <Text>{titleText}</Text>
                <div>
                    {
                        !!activityOptions && activityOptions.length > 0
                        && <Select
                            value={selectedActivityId}
                            onChange={(value) => setSelectedActivityId?.(value || undefined)}
                            options={activityOptions}
                            style={{width: 250, marginRight: 8}}
                            placeholder={`Select ${t('activity', false)}`}
                            allowClear
                            showSearch
                            optionFilterProp="label"
                            filterOption={(input, option) =>
                                ((option?.label as string) || '').toLowerCase().includes(input.toLowerCase()) ||
                                ((option?.value as string) || '').toLowerCase().includes(input.toLowerCase())
                            }
                        />
                    }
                    <Button
                        icon={ <RadarChartOutlined />}
                        type={showChart ? 'primary' : 'default'}
                        onClick={() => setShowChart(true)}
                            >
                                Chart
                    </Button>
                    <Button 
                        icon={<TableOutlined/>}
                        type={!showChart ? 'primary' : 'default'}
                        onClick={() => setShowChart(false)}
                    >
                        Table
                    </Button>    
                </div>
            </Space>
        </div>
    );
}

export const SectionPerformanceCard: FC<Props> = ({type, attemptId, activityId}): ReactElement => {
    const {t} = useLabels();
    const rendererId = useContext(LabelsContext);
    const filterPrefix = useFilterPrefix();
    const isAggregated = !attemptId;
    const [showChart, setShowChart] = useState<boolean>(isAggregated);
    const [selectedActivityId, setSelectedActivityId] = useState<string | undefined>(activityId);
    const titleText = isAggregated ? t('average_section_performance_title') : t('section_performance_title');

    const {data: activities, isLoading} = useQuerySearchParams<Activity[]>(
        '/reports/activities/',
        [filterPrefix, QueryFilter.COHORTS, QueryFilter.COURSES],
        {
            enabled: !attemptId && !activityId,
            defaultFilters: {
                fields: 'id,title',
                ...(rendererId ? {renderer: rendererId} : {}),
            },
        },
    );

    useEffect(() => {
        if (activities && activities.length) {
            if (!activities.map(a => a.id).includes(Number(selectedActivityId))) {
                // Selected option is no longer available, so reset it
                setSelectedActivityId(activities[0].id.toString());
            }
        }
    }, [activities, selectedActivityId]);

    const activityOptions = useMemo(() => {
        if (activityId || attemptId) return [];
        const options = ((activities || []) as Activity[]).map((activity) => ({
            label: activity.title,
            value: activity.id.toString(),
        }));
        return options;
    }, [activities, attemptId, activityId]);

    const Title = ( 
        !isAggregated 
        ? titleText
        : <CardTitle
            showChart={showChart}
            setShowChart={setShowChart}
            titleText={titleText}
            selectedActivityId={selectedActivityId}
            setSelectedActivityId={setSelectedActivityId}
            activityOptions={activityOptions}
        />
    )
    if (isLoading) return (
        <Card title={Title} className='chart-card'>
            <SpinContainer/>
        </Card>
    );
    

    if (showChart) {
        return <SectionPerformanceRadarPlotCard
            title={Title}
            type={type}
            activityId={selectedActivityId}
            defaultFilters={rendererId ? {renderer: rendererId} : undefined}
        />
    };

    return (
        <SectionPerformanceFirstBestTableCard
            title={Title}
            type={type}
            attemptId={attemptId}
            activityId={selectedActivityId}
            defaultFilters={rendererId ? {renderer: rendererId} : undefined}
            isAggregated={isAggregated}
        />
    )
};
